<script lang="ts">
  import { onMount } from "svelte"
  import { flip } from "svelte/animate"
  import Sortable from "sortablejs"
  import {
    toast,
    xhr,
    drag_enabled,
    wait,
    sortElementsForSortable
  } from "$app/utils"
  import Heading from "$app/components/Heading.svelte"
  import EditGoalStatus from "../Goals/EditGoalStatus.svelte"
  import SelectQuarterDropdown from "./SelectQuarterDropdown.svelte"
  import _ from "lodash"
  import Dropdown from "$app/components/Dropdown.svelte"
  import {
    EllipsisVertical,
    SquarePlus,
    SquareCheck,
    SquareX,
    Eye,
    Printer
  } from "lucide-svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  let props = $props()

  interface Props {
    goals: GoalType[]
    options: any
    periods: any
    hideCompleted: boolean
    showQuarterSelector: boolean
    headings: any
    editable: boolean
    onopenModal: (goal: GoalType) => void
    onupdate: (goal: GoalType) => void
    onupdateGoalsPeriod: (data: any) => void
    onupdateGoals: (goals: GoalType[]) => void
    onupdateHideCompleted: (hide: boolean) => void
    onopenNewGoalModal: () => void
    onopenVisionsModal: () => void
  }

  let {
    options = {},
    periods = {},
    showQuarterSelector = false,
    headings,
    editable = false,
    onopenModal,
    onupdateGoalsPeriod,
    onupdateGoals,
    onupdateHideCompleted,
    onopenNewGoalModal,
    onopenVisionsModal
  }: Props = props

  let goals = $state(props.goals)
  let hideCompleted = $state(props.hideCompleted || false)

  let dnd_enabled = editable

  innerWidth = 0
  dnd_enabled = drag_enabled(innerWidth, editable)

  let openDropdown = $state(false)

  function toggleHideCompleted() {
    hideCompleted = !hideCompleted
    onupdateHideCompleted(hideCompleted)
    openDropdown = false
  }

  const flipDurationMs = 300

  const goalCount = () => {
    if (hideCompleted) {
      return goals.filter(
        (g) => g.status !== "complete" && g.status !== "not_met"
      ).length
    } else {
      return goals.length
    }
  }

  async function saveSortOrder(newGoals: GoalType[]) {
    try {
      const resp = await xhr.post(
        `/plans/${appStore.plan.id}/goals/sort_without_department.json`,
        { goals: newGoals.map((s) => s.id) }
      )
      onupdateGoals(resp.data.goals)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the sort order", "error")
      const tmpGoals = [...goals]
      goals = []
      await wait(50)
      goals = tmpGoals
    }
  }

  function showGoalLinkage(goal: GoalType) {
    const annualGoal = document.querySelector(
      `[data-goal-id='${goal.parent_goal_id}']`
    )
    if (annualGoal) {
      annualGoal.classList.add("bg-indigo-50")
    }

    const quarterlyGoals = document.querySelectorAll(
      `[data-parent-goal-id='${goal.id}']`
    )
    if (quarterlyGoals) {
      quarterlyGoals.forEach((quarterlyGoal) => {
        quarterlyGoal.classList.add("bg-indigo-50")
      })
    }
  }
  function hideGoalLinkage(goal: GoalType) {
    const annualGoal = document.querySelector(
      `[data-goal-id='${goal.parent_goal_id}']`
    )
    if (annualGoal) {
      annualGoal.classList.remove("bg-indigo-50")
    }

    const quarterlyGoals = document.querySelectorAll(
      `[data-parent-goal-id='${goal.id}']`
    )
    if (quarterlyGoals) {
      quarterlyGoals.forEach((quarterlyGoal) => {
        quarterlyGoal.classList.remove("bg-indigo-50")
      })
    }
  }

  let sortableSection: HTMLElement

  function setSortable(): void {
    if (sortableSection) {
      Sortable.create(sortableSection, {
        group: "AnnualGoals",
        animation: 200,
        delay: 400,
        delayOnTouchOnly: true,
        direction: "vertical",
        ghostClass: "opacity-50",
        dragClass: "border-green-400",
        onEnd: async function (e: EventSortType) {
          if (e.newIndex == e.oldIndex) return
          this.options.sort = false

          try {
            await saveSortOrder(
              sortElementsForSortable(goals, e.newIndex, e.oldIndex)
            )
          } catch (e) {
          } finally {
            this.options.sort = true
          }
        }
      })
    }
  }

  onMount(async () => {
    setSortable()
  })
</script>

<div class="bg-white shadow border-l-4 border-annual-goal">
  <div class="bg-white px-4 py-4 sm:px-6 relative">
    <div class="flex items-center justify-start md:justify-center">
      <h3 class="leading-6 font-semibold text-black flex">
        <Heading
          title="Annual Goals"
          section="AnnualGoal"
          {headings}
          on:updateHeadings
        />&nbsp;- {options.fiscal_year}
      </h3>
      <div class="absolute right-3 top-3 flex items-center">
        {#if showQuarterSelector}
          <SelectQuarterDropdown {periods} {options} {onupdateGoalsPeriod} />
          <div
            class="relative inline-block text-left ml-2 border border-gray-100"
          >
            <Dropdown bind:open={openDropdown}>
              {#snippet button()}
                <div class="py-0.5 px-0.5">
                  <EllipsisVertical class="text-gray-500 fill-current size-4" />
                </div>
              {/snippet}

              <div>
                <div
                  class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
                  role="menu"
                >
                  {#if editable}
                    <div class="py-1" role="none">
                      <button
                        class="btn-dropdown flex items-center"
                        role="menuitem"
                        onclick={onopenNewGoalModal}
                      >
                        <SquarePlus class="w-4 h-4 mr-2 text-gray-600" />
                        Add Goal
                      </button>
                    </div>
                    <div
                      class="py-1 border border-t border-gray-200"
                      role="none"
                    >
                      <button
                        class="btn-dropdown flex items-center"
                        role="menuitem"
                        onclick={onopenVisionsModal}
                      >
                        <Eye class="w-4 h-4 mr-2 text-gray-600" />
                        View Visions
                      </button>
                    </div>
                  {/if}
                  <div class="py-1 border border-t border-gray-200" role="none">
                    <button
                      class="btn-dropdown flex items-center"
                      role="menuitem"
                      onclick={toggleHideCompleted}
                    >
                      {#if hideCompleted}
                        <SquareCheck class="w-4 h-4 mr-2 text-gray-600" />
                        Show Completed
                      {:else}
                        <SquareX class="w-4 h-4 mr-2 text-gray-600" />
                        Hide Completed
                      {/if}
                    </button>
                  </div>
                  <div class="py-1 border border-t border-gray-200" role="none">
                    <a
                      href={`/plans/${appStore.plan.id}.pdf?quarter=${options.quarter == "-1" ? "1" : options.quarter}&year=${options.year}`}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                      class="btn-dropdown flex items-center"
                      role="menuitem"
                      onclick={toggleHideCompleted}
                    >
                      <Printer class="w-4 h-4 mr-2 text-gray-600" />
                      Print PDF
                    </a>
                  </div>
                </div>
              </div>
            </Dropdown>
          </div>
        {/if}
      </div>
    </div>
  </div>
  {#if goalCount() === 0}
    <div class="flex justify-center items-center w-full my-10">
      <span
        class="inline-flex items-center px-4 py-2 text-base font-bold text-gray-800"
      >
        {#if hideCompleted}
          No Uncompleted Annual Goals
        {:else}
          No Annual Goals
        {/if}
      </span>
      <button
        class="rounded bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100"
        onclick={onopenNewGoalModal}
      >
        Add Goal
      </button>
    </div>
  {/if}
  <div class="flex flex-col border-t-1 border-gray-200">
    <ul class="divide-y divide-gray-200" bind:this={sortableSection}>
      {#each goals as goal, idx (goal.id)}
        <li
          animate:flip={{ duration: flipDurationMs }}
          class="block hover:bg-indigo-100 {editable
            ? 'hover:cursor-move'
            : ''}"
          data-goal-id={goal.id}
          data-parent-goal-id={goal.parent_goal_id}
          class:hidden={hideCompleted &&
            (goal.status === "complete" || goal.status === "not_met")}
          onfocus={() => showGoalLinkage(goal)}
          onmouseover={() => showGoalLinkage(goal)}
          onblur={() => hideGoalLinkage(goal)}
          onmouseout={() => hideGoalLinkage(goal)}
          ondblclick={() => onopenModal(goal)}
        >
          <div class="pl-2 pr-0 py-1 sm:pl-4">
            <div class="flex items-center justify-between select-none">
              <p
                class="text-sm font-medium text-black hover:text-indigo-600 truncate select-none"
              >
                <span class="text-gray-400 text-xs">{idx + 1}.</span>
                {goal.name}
              </p>

              <div class="mx-1 flex-shrink-0 flex items-center justify-end">
                <EditGoalStatus
                  goalOrTask={goal}
                  type="Goal"
                  show_border={false}
                  small={true}
                  {editable}
                  onupdate={(data) => (goal.status = data.status)}
                />
              </div>
            </div>
          </div>
        </li>
      {/each}
    </ul>
  </div>
</div>
